import {useCallback} from 'react';
import Clarity from '@microsoft/clarity';
import notebookTags, {NotebookTag, NotebookTagKeys} from '../../../core/models/static/routes/notebook-tags';
import EnvService from "../../../core/services/env";
import {useOperatorInformation} from "../use-operator-information";
import {usePartnerInformation} from "../use-partner-information";
import {useAuthenticationState} from "../use-auth-state";

interface BehavioralAnalyticsConfig {
    /**
     * Track custom events in Clarity using predefined event names.
     * Events are type-safe and defined in the BehavioralAnalyticsEvents object.
     *
     * @example
     * trackEvent(events => events.PRICE_MATRIX_CREATED)
     * trackEvent(events => events.FORM_SUBMIT)
     *
     * @param getEventName - Function that receives events object and returns an event name
     */
    trackEvent: (getEventName: (events: typeof BehavioralAnalyticsEvents) => BehavioralAnalyticsEventType) => void;

    /**
     * Upgrade current session with a reason to mark it as important.
     * This helps in prioritizing certain sessions in Clarity's dashboard.
     *
     * @example
     * upgradeSession('user_made_purchase')
     * upgradeSession('critical_error_occurred')
     *
     * @param reason - Reason for the session upgrade
     */
    upgradeSession: (reason: string) => void;

    /**
     * Configure the behavioral analytics tool with the given tags.
     * This function sets up Clarity with operator information and page/module tracking.
     *
     * @example
     * configureBehavioralTool(predefinedTags => [
     *   { key: NotebookTagKeys.page, value: NotebookTagPages.dashboard },
     *   { key: NotebookTagKeys.module, value: NotebookTagModules.sales }
     * ])
     *
     * @param getConfigTags - Function that receives predefined tags and returns configuration tags
     */
    configureBehavioralTool: (getConfigTags: (predefinedTags: typeof notebookTags) => NotebookTag[] | undefined) => void;
}

/**
 * Hook for integrating with Microsoft Clarity behavioral analytics.
 * Provides functionality for:
 * - Automatic page and module tracking
 * - Operator identification and information tagging
 * - Custom event tracking
 * - Session importance management
 *
 * @returns Object containing tracking and configuration functions
 */
const useBehavioralAnalyticsTool = (): BehavioralAnalyticsConfig => {
    const {authenticationToken} = useAuthenticationState();
    const operatorInformation = useOperatorInformation();
    const partnerInformation = usePartnerInformation();

    /**
     * Configures Clarity with the given tags and operator information.
     * This function handles:
     * - Setting up page and module tags
     * - Setting operator information
     * - Identifying the session
     */
    const configureBehavioralTool = useCallback((getConfigTags: (predefinedTags: typeof notebookTags) => NotebookTag[] | undefined) => {
        if (!EnvService.isProduction || !EnvService.inMainEnvironment) {
            return
        }
        const tags = getConfigTags(notebookTags);
        if (!tags?.length) return;

        // Extract page and module information from tags using NotebookTagKeys
        const pageTag = tags.find(tag => tag.key === NotebookTagKeys.page);
        const moduleTag = tags.find(tag => tag.key === NotebookTagKeys.module);
        const sectionTag = tags.find(tag => tag.key === NotebookTagKeys.section);
        const slugTag = tags.find(tag => tag.key === NotebookTagKeys.slug);
        const customerNoTag = tags.find(tag => tag.key === NotebookTagKeys.customerNo);
        const ncrCodeTag = tags.find(tag => tag.key === NotebookTagKeys.ncrCode);
        const partNoTag = tags.find(tag => tag.key === NotebookTagKeys.partNo);
        const queryTag = tags.find(tag => tag.key === NotebookTagKeys.query);

        // Get operator information from LocalStorage for session identification
        const operatorId = operatorInformation?.id ?? "";
        const operatorName = operatorInformation?.name ?? "";
        const email = operatorInformation?.email ?? "";
        const partnerName = partnerInformation?.name ?? "";
        const authToken = authenticationToken ?? "";

        // Create an operator information array for consolidated tagging
        const operatorInfo = [
            operatorName && `Name: ${operatorName}`,
            email && `Email: ${email}`,
            partnerName && `Partner: ${partnerName}`,
        ].filter(Boolean); // Remove null/undefined/empty entries

        // Set a consolidated operator information tag for easy filtering
        if (operatorInfo.length > 0) {
            Clarity.setTag('operatorInformation', operatorInfo.join(' | '));
        }

        // Identify the session with the proper parameter order:
        // 1. operatorId: Unique identifier for the operator
        // 2. authToken: Session-specific identifier
        // 3. pageTag: Current page identifier
        // 4. friendlyName: Human-readable operator name
        Clarity.identify(operatorId, authToken, pageTag?.value || '', operatorName);

        // Set all available tags for comprehensive tracking
        if (moduleTag?.value) {
            Clarity.setTag(NotebookTagKeys.module.toLowerCase(), moduleTag.value);
        }
        if (sectionTag?.value) {
            Clarity.setTag(NotebookTagKeys.section, sectionTag.value);
        }
        if (slugTag?.value) {
            Clarity.setTag(NotebookTagKeys.slug, slugTag.value);
        }
        if (customerNoTag?.value) {
            Clarity.setTag(NotebookTagKeys.customerNo, customerNoTag.value);
        }
        if (ncrCodeTag?.value) {
            Clarity.setTag(NotebookTagKeys.ncrCode, ncrCodeTag.value);
        }
        if (partNoTag?.value) {
            Clarity.setTag(NotebookTagKeys.partNo, partNoTag.value);
        }
        if (queryTag?.value) {
            Clarity.setTag(NotebookTagKeys.query, queryTag.value);
        }
    }, [authenticationToken, operatorInformation?.email, operatorInformation?.id, operatorInformation?.name, partnerInformation?.name]);

    /**
     * Tracks custom events in Clarity using predefined event names.
     * Events are type-safe and defined in the BehavioralAnalyticsEvents object.
     *
     * The callback is memoized to prevent unnecessary re-renders and
     * maintains consistent function reference across renders.
     *
     * @example
     * trackEvent(events => events.PRICE_MATRIX_CREATED)
     * trackEvent(events => events.FORM_SUBMIT)
     */
    const trackEvent = useCallback((
        getEventName: (events: typeof BehavioralAnalyticsEvents) => BehavioralAnalyticsEventType
    ) => {
        if (!EnvService.isProduction || !EnvService.inMainEnvironment) {
            return
        }
        const eventName = getEventName(BehavioralAnalyticsEvents);
        Clarity.event(eventName);
    }, []);

    /**
     * Upgrades the importance of the current session in Clarity.
     * Used to mark sessions that contain significant events or actions.
     *
     * The callback is memoized to prevent unnecessary re-renders and
     * maintains consistent function reference across renders.
     *
     * @example
     * upgradeSession('user_made_purchase')
     * upgradeSession('critical_error_occurred')
     */
    const upgradeSession = useCallback((reason: string) => {
        if (!EnvService.isProduction || !EnvService.inMainEnvironment) {
            return
        }
        Clarity.upgrade(reason);
    }, []);

    return {
        trackEvent,
        upgradeSession,
        configureBehavioralTool,
    };
};

export default useBehavioralAnalyticsTool;

export const BehavioralAnalyticsEvents = {
// Predefined event names for tracking in Clarity
} as const;

export type BehavioralAnalyticsEventType = typeof BehavioralAnalyticsEvents[keyof typeof BehavioralAnalyticsEvents];